function flutterHandler(): { callHandler: (name: string, ...args: unknown[]) => Promise<unknown> } | null {
  if (typeof window === "undefined") return null;
  const bridge = (window as unknown as { flutter_inappwebview?: { callHandler?: (name: string, ...args: unknown[]) => Promise<unknown> } })
    .flutter_inappwebview;
  return bridge?.callHandler ? { callHandler: bridge.callHandler.bind(bridge) } : null;
}

export function hasNativeBridge(): boolean {
  return flutterHandler() != null;
}

export async function waitForNativeBridge(timeoutMs = 2500): Promise<boolean> {
  if (hasNativeBridge()) return true;
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    await new Promise((resolve) => {
      if (typeof setTimeout === "function") setTimeout(resolve, 120);
      else resolve(undefined);
    });
    if (hasNativeBridge()) return true;
  }
  return hasNativeBridge();
}

export const BIOMETRIC_MESSAGES = {
  failed: "Biometric authentication failed.",
  canceled: "Biometric authentication cancelled.",
  unavailable: "Biometric authentication is not available on this device.",
} as const;

export type BiometricMethod = "fingerprint" | "face" | "generic";

export type BiometricStatus = {
  ok: boolean;
  fingerprintAvailable: boolean;
  faceAvailable: boolean;
  genericAvailable: boolean;
  fingerprintEnabled: boolean;
  faceEnabled: boolean;
  genericEnabled: boolean;
  hasCredentials: boolean;
  fingerprintLoginEnabled: boolean;
  faceLoginEnabled: boolean;
  genericLoginEnabled: boolean;
  email: string;
  faceLabel: string;
  enrolled: boolean;
  error?: string;
  needsToken?: boolean;
  canceled?: boolean;
  unavailable?: boolean;
};

function emptyStatus(): BiometricStatus {
  return {
    ok: false,
    fingerprintAvailable: false,
    faceAvailable: false,
    genericAvailable: false,
    fingerprintEnabled: false,
    faceEnabled: false,
    genericEnabled: false,
    hasCredentials: false,
    fingerprintLoginEnabled: false,
    faceLoginEnabled: false,
    genericLoginEnabled: false,
    email: "",
    faceLabel: "Face Lock",
    enrolled: false,
  };
}

function asStatus(value: unknown): BiometricStatus {
  if (!value || typeof value !== "object") return emptyStatus();
  const row = value as Record<string, unknown>;
  return {
    ok: row.ok === true,
    fingerprintAvailable: row.fingerprintAvailable === true,
    faceAvailable: row.faceAvailable === true,
    genericAvailable: row.genericAvailable === true,
    fingerprintEnabled: row.fingerprintEnabled === true,
    faceEnabled: row.faceEnabled === true,
    genericEnabled: row.genericEnabled === true,
    hasCredentials: row.hasCredentials === true,
    fingerprintLoginEnabled: row.fingerprintLoginEnabled === true,
    faceLoginEnabled: row.faceLoginEnabled === true,
    genericLoginEnabled: row.genericLoginEnabled === true,
    email: typeof row.email === "string" ? row.email.trim() : "",
    faceLabel: typeof row.faceLabel === "string" && row.faceLabel.trim() ? row.faceLabel : "Face Lock",
    enrolled: row.enrolled === true,
    error: typeof row.error === "string" ? row.error : undefined,
    needsToken: row.needsToken === true,
    canceled: row.canceled === true,
    unavailable: row.unavailable === true,
  };
}

export function biometricUserMessage(result: {
  canceled?: boolean;
  unavailable?: boolean;
  error?: string;
}): string {
  if (result.canceled) return BIOMETRIC_MESSAGES.canceled;
  if (result.unavailable) return BIOMETRIC_MESSAGES.unavailable;
  const error = (result.error || "").toLowerCase();
  if (error.includes("cancel")) return BIOMETRIC_MESSAGES.canceled;
  if (error.includes("not available") || error.includes("not enrolled") || error.includes("unavailable")) {
    return BIOMETRIC_MESSAGES.unavailable;
  }
  return BIOMETRIC_MESSAGES.failed;
}

export function publicPasswordLoginError(err: unknown): string {
  const status = err && typeof err === "object" && "status" in err ? Number((err as { status?: number }).status) : 0;
  const code =
    err instanceof Error
      ? err.message
      : err && typeof err === "object" && "message" in err
        ? String((err as { message?: unknown }).message ?? "")
        : "";
  if (status === 429 || code === "rate_limited") return "Too many attempts. Try again later.";
  if (status === 403 && (code === "setup_required" || /setup required/i.test(code))) {
    return "Account setup required. Open your invite link to set a password before signing in.";
  }
  if (status === 403 && (code === "account_disabled" || /disabled/i.test(code))) {
    return "This account is disabled or temporarily locked. Contact a Super Admin.";
  }
  return "Invalid email or password.";
}

export function publicBiometricApiError(err: unknown): string {
  const message = err instanceof Error ? err.message : "";
  if (/cancel/i.test(message)) return BIOMETRIC_MESSAGES.canceled;
  if (/not available|not enrolled/i.test(message)) return BIOMETRIC_MESSAGES.unavailable;
  return BIOMETRIC_MESSAGES.failed;
}

export async function getBiometricStatus(): Promise<BiometricStatus | null> {
  const native = flutterHandler();
  if (!native) return null;
  try {
    return asStatus(await native.callHandler("biometricStatus"));
  } catch {
    return emptyStatus();
  }
}

export async function setBiometricMethod(input: {
  method: BiometricMethod;
  enabled: boolean;
  email?: string;
  token?: string;
}): Promise<BiometricStatus> {
  const native = flutterHandler();
  if (!native) return { ...emptyStatus(), error: "Open this setting in the Dashcam app." };
  try {
    return asStatus(
      await native.callHandler("setBiometric", {
        method: input.method,
        enabled: input.enabled,
        email: input.email,
        token: input.token,
      }),
    );
  } catch {
    return { ...emptyStatus(), error: "Could not update biometric login." };
  }
}

export async function notifyNativeAppReady(): Promise<void> {
  const native = flutterHandler();
  if (!native) return;
  try {
    await native.callHandler("appReady", {});
  } catch {
    /* Splash fallback still dismisses after the web page loads. */
  }
}

export async function rememberNativeLogin(email: string, token = ""): Promise<void> {
  const native = flutterHandler();
  if (!native) return;
  try {
    await native.callHandler("rememberLogin", { email, token });
  } catch {
    /* Login must still succeed if the native bridge is unavailable. */
  }
}

export type BiometricLoginResult = {
  ok: boolean;
  email?: string;
  token?: string;
  canceled?: boolean;
  unavailable?: boolean;
  error?: string;
};

export async function authenticateBiometricLogin(method: BiometricMethod = "fingerprint"): Promise<BiometricLoginResult> {
  const native = flutterHandler();
  if (!native) return { ok: false, unavailable: true, error: BIOMETRIC_MESSAGES.unavailable };
  try {
    const value = await native.callHandler("authenticateBiometric", { method });
    if (!value || typeof value !== "object") return { ok: false, error: BIOMETRIC_MESSAGES.failed };
    const row = value as Record<string, unknown>;
    if (row.ok === true && typeof row.token === "string" && row.token.trim()) {
      return {
        ok: true,
        email: typeof row.email === "string" ? row.email : undefined,
        token: row.token,
      };
    }
    const mapped = {
      canceled: row.canceled === true,
      unavailable: row.unavailable === true,
      error: typeof row.error === "string" ? row.error : undefined,
    };
    return {
      ok: false,
      ...mapped,
      error: biometricUserMessage(mapped),
    };
  } catch {
    return { ok: false, error: BIOMETRIC_MESSAGES.failed };
  }
}

export async function updateNativeEmail(email: string): Promise<void> {
  const native = flutterHandler();
  if (!native) return;
  try {
    await native.callHandler("updateBiometricEmail", { email });
  } catch {
    /* ignore */
  }
}

export async function updateNativePassword(_password: string): Promise<void> {
  const native = flutterHandler();
  if (!native) return;
  try {
    await native.callHandler("updateBiometricPassword", {});
  } catch {
    /* ignore */
  }
}
